/**
 * Individual Legal Document Page
 *
 * Displays a single legal document with:
 * - Title and last updated date
 * - Full document content
 * - Breadcrumb navigation back to legal hub
 * - Print-friendly layout
 */

import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
  ArrowLeftIcon,
  DocumentTextIcon,
  ShieldCheckIcon,
  CheckBadgeIcon,
  GlobeAltIcon,
  ExclamationTriangleIcon,
  DocumentDuplicateIcon,
  ServerIcon,
  CreditCardIcon,
  LockClosedIcon,
  PrinterIcon,
} from '@heroicons/react/24/outline';
import { MainLayout } from '../layouts/MainLayout';
import legalDocuments from '../data/legal-documents.json';

// Map icon names to actual icon components
const iconMap: Record<string, React.ComponentType<{ className?: string }>> = {
  DocumentTextIcon,
  ShieldCheckIcon,
  CheckBadgeIcon,
  GlobeAltIcon,
  ExclamationTriangleIcon,
  DocumentDuplicateIcon,
  ServerIcon,
  CreditCardIcon,
  LockClosedIcon,
};

export default function LegalDocumentPage() {
  const { slug } = useParams<{ slug: string }>();
  const document = legalDocuments.find(doc => doc.slug === slug);

  // Handle document not found
  if (!document) {
    return (
      <MainLayout>
        <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 px-4 py-8">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-3xl font-bold text-white mb-4">Document Not Found</h1>
            <p className="text-slate-400 mb-8">
              The legal document you're looking for doesn't exist.
            </p>
            <Link
              to="/legal"
              className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
            >
              <ArrowLeftIcon className="w-5 h-5" />
              Back to Legal Hub
            </Link>
          </div>
        </div>
      </MainLayout>
    );
  }

  const IconComponent = iconMap[document.icon];

  const handlePrint = () => {
    window.print();
  };

  return (
    <MainLayout>
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 px-4 py-8 print:bg-white">
        <div className="max-w-4xl mx-auto">
          {/* Breadcrumb Navigation */}
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
            className="mb-8 print:hidden"
          >
            <Link
              to="/legal"
              className="inline-flex items-center gap-2 text-slate-400 hover:text-blue-400 transition-colors"
            >
              <ArrowLeftIcon className="w-4 h-4" />
              Back to Legal Hub
            </Link>
          </motion.div>

          {/* Document Header */}
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="mb-8"
          >
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 bg-gradient-to-br from-blue-500/20 to-purple-500/20 rounded-xl flex items-center justify-center print:hidden">
                  {IconComponent && (
                    <IconComponent className="w-8 h-8 text-blue-400" />
                  )}
                </div>
                <div>
                  <h1 className="text-4xl font-bold text-white mb-2 print:text-black">
                    {document.title}
                  </h1>
                  <p className="text-slate-400 print:text-gray-600">
                    Last updated: {document.lastUpdated}
                  </p>
                </div>
              </div>
              <button
                onClick={handlePrint}
                className="print:hidden inline-flex items-center gap-2 px-4 py-2 bg-slate-800/50 hover:bg-slate-800 border border-slate-700 rounded-lg text-slate-300 hover:text-white transition-all"
                aria-label="Print document"
              >
                <PrinterIcon className="w-5 h-5" />
                Print
              </button>
            </div>
            <p className="text-lg text-slate-300 print:text-gray-700">
              {document.description}
            </p>
          </motion.div>

          {/* Document Content */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="bg-slate-800/30 backdrop-blur-sm border border-slate-700/50 rounded-2xl p-8 print:bg-white print:border-gray-200"
          >
            <div className="prose prose-invert prose-lg max-w-none print:prose-slate prose-headings:font-bold prose-headings:text-white print:prose-headings:text-gray-900 prose-h1:text-3xl prose-h2:text-2xl prose-h2:mt-8 prose-h2:mb-4 prose-h3:text-xl prose-h3:mt-6 prose-h3:mb-3 prose-h4:text-lg prose-h4:mt-4 prose-h4:mb-2 prose-p:text-slate-300 print:prose-p:text-gray-700 prose-p:leading-relaxed prose-p:mb-4 prose-ul:text-slate-300 print:prose-ul:text-gray-700 prose-ol:text-slate-300 print:prose-ol:text-gray-700 prose-li:my-2 prose-strong:text-white print:prose-strong:text-gray-900 prose-strong:font-semibold prose-a:text-blue-400 print:prose-a:text-blue-600 prose-a:no-underline hover:prose-a:underline">
              {document.content ? (
                <ReactMarkdown remarkPlugins={[remarkGfm]}>
                  {document.content}
                </ReactMarkdown>
              ) : (
                <div className="space-y-6">
                  <p className="text-sm text-slate-500 print:text-gray-500 italic border-l-4 border-blue-500 pl-4">
                    This document content is being processed and will be available shortly.
                  </p>
                  <div className="mt-8">
                    <p>
                      For immediate access to this document, please contact us through the Support channel in your account.
                    </p>
                  </div>
                </div>
              )}
            </div>
          </motion.div>

          {/* Footer Navigation */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.3 }}
            className="mt-8 print:hidden"
          >
            <Link
              to="/legal"
              className="inline-flex items-center gap-2 px-6 py-3 bg-slate-800/50 hover:bg-slate-800 border border-slate-700 rounded-lg text-slate-300 hover:text-white transition-all"
            >
              <ArrowLeftIcon className="w-5 h-5" />
              Back to Legal Hub
            </Link>
          </motion.div>

          {/* Contact Information */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.4 }}
            className="mt-12 text-center print:mt-8"
          >
            <p className="text-slate-500 text-sm print:text-gray-600">
              Questions about this document? Contact us through the Support channel in your account.
            </p>
          </motion.div>
        </div>
      </div>
    </MainLayout>
  );
}
